import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./Auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatcherPanel = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    if (user?.role === "admin") {
      fetchRequests();
    }
  }, [user]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests`);
      const active = response.data.filter(r => 
        ["pending", "assigned", "en_route", "in_progress"].includes(r.status)
      );
      setRequests(active);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (requestId) => {
    try {
      await axios.post(`${API}/dispatcher/send-reminder?request_id=${requestId}`);
      alert("✅ Reminder sent!");
    } catch (error) {
      alert("❌ Failed to send reminder");
    }
  };

  const sendCustomUpdate = async (requestId) => {
    if (!customMessage.trim()) {
      alert("Please enter a message");
      return;
    }

    try {
      await axios.post(`${API}/dispatcher/update-all-parties?request_id=${requestId}&message=${encodeURIComponent(customMessage)}`);
      alert("✅ Update sent!");
      setCustomMessage("");
      setSelectedRequest(null);
    } catch (error) {
      alert("❌ Failed to send update");
    }
  };

  const setETA = async (requestId, minutes) => {
    try {
      await axios.post(`${API}/requests/${requestId}/set-eta?eta_minutes=${minutes}`);
      alert(`✅ ETA set to ${minutes} minutes`);
      fetchRequests();
    } catch (error) {
      alert("❌ Failed to set ETA");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="requests-screen">
        <div className="empty-state">
          <div className="empty-state-icon">🔒</div>
          <div className="empty-state-text">Admin access required</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="requests-screen" data-testid="dispatcher-panel">
      <div className="requests-header">
        <h1 className="requests-title">Dispatcher</h1>
        <p className="requests-subtitle">Manage active jobs</p>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '8px', 
        marginBottom: '20px' 
      }}>
        <div className="payment-card" style={{ padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--accent)' }}>{requests.length}</div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Active</div>
        </div>
        <div className="payment-card" style={{ padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#fbbf24' }}>
            {requests.filter(r => r.status === "pending").length}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Pending</div>
        </div>
        <div className="payment-card" style={{ padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#60a5fa' }}>
            {requests.filter(r => r.status === "assigned").length}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Assigned</div>
        </div>
        <div className="payment-card" style={{ padding: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#4ade80' }}>
            {requests.filter(r => ["en_route", "in_progress"].includes(r.status)).length}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Working</div>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">No active jobs</div>
        </div>
      ) : (
        <div>
          {requests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div>
                  <div className="request-service">
                    {request.urgency === "emergency" && "🚨 "}
                    {request.service_category}
                  </div>
                  <div className="request-customer">{request.customer_name}</div>
                </div>
                <span className={`status-badge status-${request.status}`}>
                  {request.status.replace("_", " ")}
                </span>
              </div>

              <div className="request-details">
                <p>📱 {request.customer_phone}</p>
                {request.customer_address && <p>📍 {request.customer_address}</p>}
                <p>👷 {request.assigned_provider_name || request.selected_provider_name || "No provider"}</p>
                <p style={{ marginTop: '4px', fontStyle: 'italic' }}>{request.description}</p>
                {request.estimated_arrival_time && (
                  <p style={{ color: 'var(--accent)', marginTop: '8px' }}>
                    ⏰ ETA: {new Date(request.estimated_arrival_time).toLocaleTimeString()}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="request-actions" style={{ flexWrap: 'wrap' }}>
                <button 
                  className="action-btn action-btn-primary"
                  onClick={() => sendReminder(request.id)}
                >
                  📢 Remind
                </button>
                <button 
                  className="action-btn action-btn-secondary"
                  onClick={() => {
                    const minutes = prompt("ETA in minutes:", "30");
                    if (minutes) setETA(request.id, parseInt(minutes));
                  }}
                >
                  ⏰ Set ETA
                </button>
                <button 
                  className="action-btn action-btn-secondary"
                  onClick={() => setSelectedRequest(selectedRequest === request.id ? null : request.id)}
                >
                  ✉️ Message
                </button>
              </div>

              {/* Custom Message */}
              {selectedRequest === request.id && (
                <div style={{ marginTop: '12px' }}>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Message for customer & provider..."
                    className="form-group"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '12px',
                      border: '1px solid var(--line)',
                      background: 'var(--dark-red)',
                      color: 'var(--text)',
                      minHeight: '80px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div className="request-actions">
                    <button 
                      className="action-btn action-btn-primary"
                      onClick={() => sendCustomUpdate(request.id)}
                    >
                      Send
                    </button>
                    <button 
                      className="action-btn action-btn-secondary"
                      onClick={() => {
                        setSelectedRequest(null);
                        setCustomMessage("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DispatcherPanel;
